import assert from './assert'
import { stringify } from 'qs'
import { get } from 'lodash'

export const workspaceRoot = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}`
}

export const workspacePath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/overview`
}

export const welcomePath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/welcome`
}

export const workspaceHomePath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/home`
}

export const setupPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/setup`
}

export const setupStepPath = args => {
  const { workspaceSlug } = args
  const path = `/${workspaceSlug}/setup`

  if (args.query) {
    const step = get(args.query, 'step', '')
    const source = get(args.query, 'source')
    const view = get(args.query, 'view', 'auto-detect')

    const stepQuery = stringify({ step })
    const sourceQueryStr = source ? '&' + stringify({ source, view }) : ''

    return `${path}?${stepQuery}${sourceQueryStr}`
  }

  return path
}

export const workspacesPath = () => '/workspaces'

export const createWorkspacePath = () => '/workspaces/new'

export const deleteWorkspacePath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/basic/delete`
}

export const settingsPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/basic`
}

export const settingsPlanSelectPath = args => {
  assert(args, ['workspaceSlug', 'planId'])
  return `/${args.workspaceSlug}/settings/change-plan/${args.planId}/select`
}

export const settingsPlanConfirmPath = args => {
  assert(args, ['workspaceSlug', 'planId'])
  return `/${args.workspaceSlug}/settings/change-plan/${args.planId}/confirm`
}

export const settingsPlanCompletePath = args => {
  assert(args, ['workspaceSlug', 'planId'])
  return `/${args.workspaceSlug}/settings/change-plan/${args.planId}/thanks`
}

export const settingsPlanDowngradePath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/plans/downgrade`
}

export const upgradePath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/upgrade`
}

export const settingsPlansPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/plans`
}

export const legacySettingsUsagePath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/api-usage`
}

export const settingsUsagePath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/usage`
}

// TODO: Rewire links from usage to this, changing period -> range
export const settingsUsageInProgressPath = args => {
  assert(args, 'workspaceSlug')

  // Temporarily support both `/usage` and `/new-usage` while we roll this out
  // This will let us redirect from `/insights` -> `/usage`
  const base = args.shouldSeeNewUsagePage
    ? `/${args.workspaceSlug}/settings/usage`
    : `/${args.workspaceSlug}/settings/new-usage`

  const query = stringify({
    period: args.range || args.period,
    metric: args.metric
  })

  if (query) {
    return `${base}?${query}`
  }

  return base
}

export const settingsAdvancedUsagePath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/advanced-usage`
}

export const settingsCardPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/card`
}

export const settingsConnectionsPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/connections`
}

export const settingsConnectionSetupSelectPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/setup-connection/select`
}

export const settingsConnectionSetupConfigurePath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/setup-connection/configure-connection`
}

export const settingsConnectionSetupIdpPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/setup-connection/configure-idp`
}

export const settingsConnectionSetupDomains = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/setup-connection/configure-domains`
}

export const settingsConnectionSetupCompletePath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/setup-connection/complete`
}

export const settingsNotificationsPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/notifications`
}

export const settingsDeliveryAlertSubscriptionsPath = args => {
  const rootPath = settingsNotificationsPath(args)
  if (args.resource) {
    return `${rootPath}/delivery-alerts?resource=${args.resource}`
  }

  return `${rootPath}/delivery-alerts`
}

export const settingsPaymentsPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/payments`
}

export const settingsAdvancedPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/advanced`
}

export const settingsPrivacySecurityPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/privacy-security`
}

export const settingsTeamPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/team`
}

export const settingsResourceManagementPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/sources-environments`
}

export const settingsLabelManagementPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/label-management`
}

export const billingDashboardPath = args => {
  assert(args, 'workspaceSlug')
  return `https://billing-dashboard.segment.com/workspace?slug=${args.workspaceSlug}`
}

export const salesPath = () => 'https://segment.com/contact/sales'

export const connectionHealthPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/integration-health`
}
export const accessManagementPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/access-management`
}

export const supportAccessPath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/support-access`
}

export const teamPermissionInvitePath = args => {
  assert(args, 'workspaceSlug')
  return `/${args.workspaceSlug}/settings/access-management/invite`
}
